// Shared control primitives for the toolbar and Inspector.
// Moved out of StudioCoreEditor.jsx during the toolbar-v3 restructure.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX } from '../toolbarIcons';
import { PaintField } from './PaintPicker';

export const TbButton = ({ icon, label, onClick, active, danger, children }) => (
  <button
    type="button"
    className={`cce-tb-btn${active ? ' active' : ''}${danger ? ' danger' : ''}`}
    onClick={onClick}
    title={label}
    aria-label={label}
  >
    {icon || children}
  </button>
);

export const TbDivider = () => <div className="cce-tb-divider" />;

export const TbPopover = ({ title, onClose, children, wide }) => {
  const { t } = useTranslation('editor');
  return (
    <div className={`cce-tb-popover${wide ? ' wide' : ''}`} role="dialog" aria-label={title}>
      <div className="cce-tb-popover-head">
        <span>{title}</span>
        <button type="button" className="cce-tb-popover-close" aria-label={t('common:actions.close')} onClick={onClose}>
          <IconX width={12} height={12} />
        </button>
      </div>
      {children}
    </div>
  );
};

// TbColorField — SAME prop signature as the old bare <input type=color>
// (label/value/onChange(hex)), now a thin wrapper over the house PaintChip +
// PaintPicker standard (solid-only popover) so every remaining call site —
// highlight custom, shadow color, emboss/3D panels, ribbon fallbacks —
// inherits the modern picker without churning. onChange still fires a plain
// hex per pick AND per drag tick, exactly like the native input's stream;
// hosts keep their existing commit modes.
// OPAQUE by contract: this field's promise is '#rrggbb' (emboss integer-RGB
// math, three.js 3D colors and every legacy consumer assume it), so the v2
// Opacity bar is hidden here and emissions stay 6-digit hex. Paint opacity
// lives on the rich FillValue hosts.
export const TbColorField = ({ label, value, onChange }) => (
  <PaintField
    label={label}
    value={typeof value === 'string' ? value : '#000000'}
    allow={['solid']}
    opaque
    chipClassName="cce-tb-color-chip"
    onChange={(next) => { if (typeof next === 'string') onChange(next); }}
  />
);

// Number input that tolerates a transiently-empty field: the old inputs did
// `parseInt(v) || fallback`, so clearing the box snapped the object to the
// fallback value mid-edit.
export const TbNumberField = ({ label, value, onChange, min = 0, max = 9999, step = 1 }) => {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="number"
      className="cce-tb-number"
      value={draft !== null ? draft : value}
      min={min}
      max={max}
      step={step}
      aria-label={label}
      title={label}
      onFocus={(e) => setDraft(e.target.value)}
      onChange={(e) => {
        setDraft(e.target.value);
        const v = parseFloat(e.target.value);
        if (!Number.isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
      }}
      onBlur={() => setDraft(null)}
    />
  );
};

// onCommit fires once when the drag ends (pointer-up / blur) so a slider
// drag lands as ONE undo entry while onChange previews live per tick.
export const TbSlider = ({ label, value, onChange, onCommit, min, max, step }) => (
  <div className="cce-tb-slider-row">
    <span>{label}</span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      aria-label={label}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      onPointerUp={onCommit}
      onBlur={onCommit}
    />
  </div>
);

// Pill group — one always-active choice out of a few (letter case, blend,
// fill type, outline position).
// Options may carry `title` (sticker shape glyphs, 2026-07-20): it becomes
// the button's tooltip AND accessible name — a pure-glyph label ('●') is no
// screen-reader name. Inert for every option without it.
export const TbSegmented = ({ label, value, options, onChange }) => (
  <div className="cce-segmented" role="group" aria-label={label} title={label}>
    {options.map((o) => (
      <button
        key={String(o.value)}
        type="button"
        className={`cce-segmented-btn${value === o.value ? ' active' : ''}`}
        title={o.title || undefined}
        aria-label={o.title || undefined}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Collapsible section with the inspector's 11px/600/uppercase head style.
// Open/closed is pure UI state — never styling state.
// Controlled mode: pass `open` (anything but undefined) + `onToggle` and the
// parent owns the state (exclusive groups); leave `open` undefined for the
// original self-managed behavior — existing call sites are untouched.
// `dot` (2026-07-19 accordion sidebar): truthy renders a small ACTIVE dot on
// the head — the all-collapsed wall must not hide which sections carry live
// non-default state (a bevel, a highlight, a list…). Derived by the caller
// from its selection mirrors; purely presentational here.
export const TbAccordion = ({ title, children, defaultOpen = false, open, onToggle, dot = false }) => {
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const controlled = open !== undefined;
  const isOpen = controlled ? open : selfOpen;
  return (
    <div className="cce-accordion">
      <button
        type="button"
        className="cce-accordion-head"
        aria-expanded={isOpen}
        onClick={() => (controlled ? onToggle?.() : setSelfOpen((o) => !o))}
      >
        <span>{title}</span>
        <span className="cce-accordion-head-side">
          {!!dot && <span className="cce-accordion-dot" title="In use" aria-hidden="true" />}
          <span className={`cce-accordion-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">▾</span>
        </span>
      </button>
      {isOpen && <div className="cce-accordion-body">{children}</div>}
    </div>
  );
};

// Row of color swatches; optional leading "None" chip when onClear is given.
export const TbSwatchRow = ({ colors, value, onPick, onClear }) => (
  <div className="cce-swatch-row">
    {onClear && (
      <button
        type="button"
        className={`cce-fx-chip${value == null ? ' active' : ''}`}
        onClick={onClear}
      >
        None
      </button>
    )}
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        className={`cce-tb-swatch${value?.toLowerCase?.() === c.toLowerCase() ? ' active' : ''}`}
        style={{ backgroundColor: c }}
        title={c}
        aria-label={`Color ${c}`}
        onClick={() => onPick(c)}
      />
    ))}
  </div>
);

export const BG_PRESETS = ['#ffffff', '#f8fafc', '#fef9c3', '#dcfce7', '#dbeafe', '#fce7f3', '#1e293b', '#111827', '#7c3aed', '#0ea5e9'];
